import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCoverageOrderLabel,useOrganizedPatientCoverages } from '@features/patient-coverages';
import { Add, WarningAmber } from '@mui/icons-material';
import { Box, Button, ClickAwayListener, Divider, IconButton, Link, Paper, Popper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { useTracking } from '../../../context/TrackingContext';
import { EventType } from '../../../types/events';
import { selectWarningDismissed } from '../store/selectors';
import { dismissWarning } from '../store/slice';

export interface EligibilityWarningIconProps {
  /** Patient ID for analytics context and fetching coverages */
  patientId?: string;
  /** Source location where icon is shown */
  source: 'patient_overview' | 'note_metadata';
  /** Callback to open insurance modal */
  onAddInsurance?: () => void;
}

/**
 * EligibilityWarningIcon Component
 *
 * Displays a warning icon next to patient names that shows a popover on hover
 * with options to learn more about eligibility checks or dismiss the warning.
 * The dismissed state persists via Redux with redux-persist.
 */
export const EligibilityWarningIcon: React.FC<EligibilityWarningIconProps> = ({
  patientId,
  source,
  onAddInsurance,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { eligibilityChecks } = useEventTracking();
  const { trackEvent } = useTracking();

  // Fetch patient coverages
  const { organized } = useOrganizedPatientCoverages(patientId || '');
  const hasCoverages = !!(organized.primary || organized.secondary || organized.tertiary);

  // True if any coverage was imported with low-confidence payer resolution
  const payerNeedsReview = [organized.primary, organized.secondary, organized.tertiary]
    .some((c) => c?.payer_resolution_needs_review === true);

  // Check if warning has been dismissed
  const warningDismissed = useAppSelector(selectWarningDismissed);

  // Hover state and refs
  const [isOpen, setIsOpen] = useState(false);
  const [hasViewed, setHasViewed] = useState(false);
  const [hasTrackedPopover, setHasTrackedPopover] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track view on mount
  useEffect(() => {
    if (!warningDismissed && !hasViewed) {
      eligibilityChecks.warningViewed({
        patient_id: patientId,
        source,
      });
      setHasViewed(true);
    }
  }, [warningDismissed, hasViewed, patientId, source, eligibilityChecks]);

  // Track popover opened once per hover session
  useEffect(() => {
    if (isOpen && !hasTrackedPopover) {
      eligibilityChecks.warningPopoverOpened({
        patient_id: patientId,
        source,
      });
      setHasTrackedPopover(true);
    }
    if (!isOpen) {
      setHasTrackedPopover(false);
    }
  }, [isOpen, hasTrackedPopover, patientId, source, eligibilityChecks]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Handle mouse enter - cancel any pending close and open
  const handleMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  }, []);

  // Handle mouse leave - delay close to allow moving to popover
  const handleMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150); // 150ms delay gives time to move to popover
  }, []);

  // Handle learn more click
  const handleLearnMoreClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      eligibilityChecks.warningLearnMoreClicked({
        patient_id: patientId,
        source,
      });
      trackEvent(EventType.EXPRESSED_INTEREST, {
        feature: 'eligibility-checks',
      });
      setIsOpen(false);
      navigate('/eligibility-checks');
    },
    [patientId, source, navigate, eligibilityChecks, trackEvent],
  );

  // Handle dismiss click
  const handleDismissClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      eligibilityChecks.warningDismissed({
        patient_id: patientId,
        source,
      });
      trackEvent(EventType.EXPRESSED_NOT_INTERESTED, {
        feature: 'eligibility-checks',
      });
      dispatch(dismissWarning());
      setIsOpen(false);
    },
    [patientId, source, dispatch, eligibilityChecks, trackEvent],
  );

  // Handle click away to close
  const handleClickAway = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Don't render if eligibility warning dismissed AND no payer verification issues
  if (warningDismissed && !payerNeedsReview) {
    return null;
  }

  return (
    <>
      <IconButton
        ref={anchorRef}
        size="small"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          p: 0.5,
          ml: 0.5,
          color: theme.palette.warning.main,
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
        aria-label="Insurance eligibility warning"
      >
        <WarningAmber fontSize="small" />
      </IconButton>

      <Popper
        open={isOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}
        sx={{ zIndex: theme.zIndex.tooltip }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            elevation={8}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
              p: 2,
              minWidth: 280,
              maxWidth: 320,
              borderRadius: 2,
            }}
          >
          <Stack spacing={1.5}>
            {/* Payer verification section — shown when any coverage needs review */}
            {payerNeedsReview && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <WarningAmber
                  sx={{ color: theme.palette.warning.main, mt: 0.25, flexShrink: 0 }}
                  fontSize="small"
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Payer verification needed
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    One or more insurers were matched automatically with low confidence. Verify the payer is correct before submitting claims.
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Eligibility section — shown when warning not yet dismissed */}
            {!warningDismissed && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                {!payerNeedsReview && (
                  <WarningAmber
                    sx={{ color: theme.palette.warning.main, mt: 0.25, flexShrink: 0 }}
                    fontSize="small"
                  />
                )}
                <Box sx={{ flexGrow: 1 }}>
                  {!payerNeedsReview && (
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Insurance not verified
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    {hasCoverages
                      ? 'Run eligibility checks before appointments to verify coverage.'
                      : 'Add insurance information to verify patient eligibility.'}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Insurance on file */}
            {hasCoverages ? (
              <>
                <Divider />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}>
                    On File
                  </Typography>
                  {organized.primary && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {getCoverageOrderLabel(1)}: {organized.primary.payer_name || 'Unknown Payer'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {organized.primary.member_id ? `Member ID: ${organized.primary.member_id}` : 'No member ID'}
                        {' · '}
                        <Box component="span" sx={{
                          color: organized.primary.status === 'active' ? 'success.main' : 'warning.main',
                          textTransform: 'capitalize',
                        }}>
                          {organized.primary.status || 'pending'}
                        </Box>
                      </Typography>
                      {organized.primary.payer_resolution_needs_review && (
                        <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 500 }}>
                          ⚠ Payer unverified — please confirm
                        </Typography>
                      )}
                    </Box>
                  )}
                  {organized.secondary && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {getCoverageOrderLabel(2)}: {organized.secondary.payer_name || 'Unknown Payer'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {organized.secondary.member_id ? `Member ID: ${organized.secondary.member_id}` : 'No member ID'}
                      </Typography>
                      {organized.secondary.payer_resolution_needs_review && (
                        <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 500 }}>
                          ⚠ Payer unverified — please confirm
                        </Typography>
                      )}
                    </Box>
                  )}
                  {organized.tertiary && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {getCoverageOrderLabel(3)}: {organized.tertiary.payer_name || 'Unknown Payer'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {organized.tertiary.member_id ? `Member ID: ${organized.tertiary.member_id}` : 'No member ID'}
                      </Typography>
                      {organized.tertiary.payer_resolution_needs_review && (
                        <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 500 }}>
                          ⚠ Payer unverified — please confirm
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
                <Divider />
              </>
            ) : (
              !warningDismissed && (
                <>
                  <Divider />
                  <Button
                    startIcon={<Add />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddInsurance?.();
                      setIsOpen(false);
                    }}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{ textTransform: 'none' }}
                  >
                    Add Insurance
                  </Button>
                </>
              )
            )}

            {/* Eligibility prompts — only when warning not dismissed */}
            {!warningDismissed && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link
                  component="button"
                  onClick={handleLearnMoreClick}
                  sx={{
                    textAlign: 'left',
                    textDecoration: 'none',
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Learn more about eligibility checks →
                </Link>
                <Link
                  component="button"
                  onClick={handleDismissClick}
                  sx={{
                    textAlign: 'left',
                    textDecoration: 'none',
                    color: theme.palette.text.secondary,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Not interested
                </Link>
              </Box>
            )}
          </Stack>
        </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

